import { useMemo, createElement } from "react";
import { orderBy, pick } from "lodash-es";
import { useAtom } from "jotai";
import { Pane as LeafletPane, WMSTileLayer } from "react-leaflet";
import { activeOverlayersAtom } from "src/atoms/overlayers";
import { LayerManagerContext, Overlayer } from "src/types";
import { filterActive, filterByContext } from "src/utils/layer-manager.utils";
import { isWMSOverlayer, isComponentOverlayer } from "src/utils/overlayer.utils";
import { pipe } from "../..//utils/fp.utils";

interface Props {
  context: LayerManagerContext;
}

export function ActiveOverlayersOnMap(props: Props) {
  const [activeLayers] = useAtom(activeOverlayersAtom);

  /**
   * Memo
   */
  const overlayers = useMemo((): Overlayer[] => {
    const sort = (overlayers: Overlayer[]) => orderBy(overlayers, "zIndex", "desc");

    return pipe(filterActive, filterByContext(props.context), sort)(activeLayers);
  }, [activeLayers, props.context]);

  return render();

  function render() {
    return (
      <>
        {overlayers.map((overlayer, i) => {
          const pane = `layer-manager-item-${overlayer.key}`;

          return (
            <LeafletPane
              key={[overlayer.key, overlayer.zIndex].join()}
              name={pane}
              style={{
                zIndex: zIndexforOverlayer(overlayer) - i,
              }}
            >
              <LayerOnMap key={overlayer.key} overlayer={overlayer} pane={pane} />;
            </LeafletPane>
          );
        })}
      </>
    );
  }
}

function zIndexforOverlayer(overlayer: Overlayer) {
  if (overlayer.zIndex && overlayer.zIndex > 400) return overlayer.zIndex;
  if (overlayer.zIndex && overlayer.zIndex < 400) {
    return 400 + overlayer.zIndex;
  } else {
    return 400;
  }
}

function LayerOnMap(props: { overlayer: Overlayer; pane: string }) {
  const { overlayer } = props;

  if (isWMSOverlayer(overlayer)) {
    // pick properties to spread into WMSTileLayer
    const supportedWMSParams = pick(props.overlayer, ["viewparams", "styles", "params", "sld", "tiled"]);
    const key = [overlayer.key, overlayer.sld].join("-");

    return (
      <WMSTileLayer
        key={key}
        url="/geoserver/wms"
        opacity={overlayer.opacity}
        layers={overlayer.layers}
        format="image/png"
        transparent
        // @ts-ignore
        tiled={true}
        {...supportedWMSParams}
      />
    );
  } else if (isComponentOverlayer(overlayer)) {
    return createElement(overlayer.component, {
      opacity: overlayer.opacity,
      pane: props.pane,
    });
  } else {
    console.warn("Unknown type of overlayer");
    return null;
  }
}
